// Navbar.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  AppBar,
  IconButton,
  Typography,
  Grid,
  Menu,
  MenuItem,
  Toolbar,
  ListItemIcon,
  ListItemText,
  MenuList,
  Slide,
} from '@mui/material';
import posthog from '../../../../utils/posthog';

import navLogo from '../../../../assets/logos/hertex-logo-long.png';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Clear,
  KeyboardArrowDown,
  KeyboardArrowUp,
  SearchOutlined,
  PersonOutlined,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import SearchModal from '../../modal/SearchModal';
import { fetchNavigationTree, NavTreeNode } from '../../../../api/apiNavigation';
import { slugify } from '../../../../section/CMS/api/cmsApi';
import { SHOW_REGISTER_NAV, isRegisterNavLabel } from '../navConfig';

// Top-level custom CMS pages are surfaced as nav items (mirroring trade). The
// "New Arrival" page is shown simply as "NEW"; match the dedicated new_arrival
// category type, or any node whose name resolves to the new-arrival page.
const isNewArrivalNav = (
  name?: string,
  displayName?: string,
  categoryType?: string,
): boolean => {
  if (categoryType === 'new_arrival') return true;
  const norm = (s?: string) => (s || '').toLowerCase().replace(/[\s_]+/g, '-');
  return [name, displayName].some((v) => norm(v).includes('new-arrival'));
};

function Navbar() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [displayMegaMenu, setDisplayMegaMenu] = useState<boolean>(false);
  const [displayMobileItemMenu, setDisplayMobileItemMenu] = useState<boolean>(false);
  const [menuContent, setMenuContent] = useState<string[]>([]);
  const [selectedPage, setSelectedPage] = useState<string | null>(null);
  const [fabricsContent, setFabricsContent] = useState<string[]>([]);
  const [wallpaperContent, setWallpaperContent] = useState<string[]>([]);
  const [brandsContent, setBrandsContent] = useState<string[]>([]);
  const [companyAddOns, setCompanyAddOns] = useState<string[]>([]);
  const [navigationItems, setNavigationItems] = useState<NavTreeNode[]>([]);
  // Top-level custom CMS pages (e.g. "New Arrival" -> "NEW", "Outdoor") surfaced
  // as nav items, mirroring trade's CMS-driven nav. { key: category_name }.
  const [customNavPages, setCustomNavPages] = useState<
    { key: string; label: string; route: string; isNewArrival: boolean }[]
  >([]);

  enum ItemGroupCode {
    FABRIC = '005',
    WALLPAPER = '001',
  }

  const handleTradeLoginClick = () => {
    const currentHostname = window.location.hostname;
    const currentPath = window.location.pathname;
    const currentSearch = window.location.search;
    const fullPath = `${currentPath}${currentSearch}`;

    let tradeBaseUrl = '';
    if (currentHostname.includes('hertex-staging.net')) {
      tradeBaseUrl = 'https://trade.hertex-staging.net';
    } else {
      tradeBaseUrl = 'https://trade.hertex.co.za';
    }

    window.open(`${tradeBaseUrl}${fullPath}`, '_blank');
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleOpenModal = () => {
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    queryClient.invalidateQueries('search-term');
  };

  // Fetch navigation tree from CMS
  useQuery('navigationTree', fetchNavigationTree, {
    onSuccess: (data) => {
      // Filter to the groups whose sub-items we build from the CMS.
      // 'Company' is included so its nav add-ons (e.g. Register) resolve.
      const filteredData = data.filter((item: NavTreeNode) =>
        item.category_name === 'Fabric' ||
        item.category_name === 'Wallpaper' ||
        item.category_name === 'Brands' ||
        item.category_name === 'Company'
      );
      setNavigationItems(filteredData);

      // Surface top-level custom CMS pages as nav items, like trade's CMS-driven
      // nav — e.g. "New Arrival" (shown as "NEW"), and "Outdoor" once it becomes a
      // custom page. They sit outside the hardcoded nav filter above, so detect
      // them from the full tree, ordered by the CMS position. Derive each slug from
      // the exact category_name so it round-trips to the CMS page on /:slug.
      const customPages = data
        .filter(
          (item: NavTreeNode) =>
            item.category_type === 'custom' &&
            (item as any).nav_bar_display !== false
        )
        .sort((a: NavTreeNode, b: NavTreeNode) =>
          (a.position_order ?? 999) - (b.position_order ?? 999)
        )
        .map((node: NavTreeNode) => {
          const isNew = isNewArrivalNav(node.category_name, node.display_name, node.category_type);
          // Public URL is based on display_name so the consumer + trade versions
          // of a page (different category_name, same display_name) share one slug.
          // Falls back to category_name when no display_name is set.
          const slugSource = node.display_name || node.category_name;
          const slug = slugify(slugSource);
          return {
            key: node.category_name,
            label: isNew ? 'NEW' : (node.display_name || node.category_name),
            route: `/${slug}`,
            isNewArrival: isNew,
          };
        });
      setCustomNavPages(customPages);

      // Populate menu content from CMS data
      filteredData.forEach(item => {
        if (item.category_name === 'Fabric' && item.children) {
          const allChildren = item.children
            .filter(child => {
              if (child.category_type !== 'nav_add_on') {
                return child.is_published;
              }
              return (
                child.is_active === true &&
                child.consumer_use_switch === 'on' &&
                (child.consumer_link_url || child.consumer_link_category_id)
              );
            })
            .sort((a, b) => {
              const orderA = a.position_order ?? 999;
              const orderB = b.position_order ?? 999;
              return orderA - orderB;
            })
            .map(child => child.display_name || child.category_name);

          setFabricsContent(allChildren);
        }
        if (item.category_name === 'Wallpaper' && item.children) {
          const allChildren = item.children
            .filter(child => {
              if (child.category_type !== 'nav_add_on') {
                return child.is_published;
              }
              return (
                child.is_active === true &&
                child.consumer_use_switch === 'on' &&
                (child.consumer_link_url || child.consumer_link_category_id)
              );
            })
            .sort((a, b) => {
              const orderA = a.position_order ?? 999;
              const orderB = b.position_order ?? 999;
              return orderA - orderB;
            })
            .map(child => child.display_name || child.category_name);

          setWallpaperContent(allChildren);
        }
        if (item.category_name === 'Brands' && item.children) {
          const brandNames = item.children
            // Only actual brand nodes — never let other category types
            // (item_group, group, etc. e.g. Rugs, Furniture) slip in
            .filter(child => child.category_type === 'brand' && child.is_published)
            .map(child => child.display_name || child.category_name);

          // Internationals is a curated page, always pinned (deduped)
          if (!brandNames.some(name => name.toLowerCase() === 'internationals')) {
            brandNames.push('Internationals');
          }
          brandNames.sort((a, b) => a.localeCompare(b));

          setBrandsContent(brandNames);
        }
        if (item.category_name === 'Company' && item.children) {
          // Only the CMS nav add-ons (e.g. Register) — the fixed Company
          // items (About Us, etc.) stay hard-coded below.
          const addOns = item.children
            .filter(
              (child) =>
                child.category_type === 'nav_add_on' &&
                child.is_active === true &&
                child.consumer_use_switch === 'on' &&
                (child.consumer_link_url || child.consumer_link_category_id)
            )
            .sort((a, b) => {
              const orderA = a.position_order ?? 999;
              const orderB = b.position_order ?? 999;
              return orderA - orderB;
            })
            .map((child) => child.display_name || child.category_name)
            .filter((label) => SHOW_REGISTER_NAV || !isRegisterNavLabel(label));

          setCompanyAddOns(addOns);
        }
      });
    },
    onError: (error) => {
      console.error('Error fetching navigation tree:', error);
      setFabricsContent(['Curtaining', 'Upholstery']);
      setWallpaperContent(['Wallpaper Calculator', 'Hertex Wallpaper']);
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const customNavByKey = Object.fromEntries(customNavPages.map((c) => [c.key, c]));

  // "New Arrival" ("NEW") leads the nav; other custom pages (e.g. Outdoor) sit
  // where the old hardcoded Outdoor did — between Homeware and Brands.
  const newArrivalKeys = customNavPages.filter((c) => c.isNewArrival).map((c) => c.key);

  const navBeforeCustom = ['Fabric', 'Wallpaper', 'Rugs', 'Homeware'];
  const navAfterCustom = ['Brands', 'Company'];
  const hardcodedNavKeys = [...navBeforeCustom, ...navAfterCustom];

  // A custom page named like a hardcoded entry (e.g. a custom "Fabric" page that
  // replaces the Fabric item group) takes over that entry via customNavByKey, so
  // don't list it a second time.
  const otherCustomKeys = customNavPages
    .filter((c) => !c.isNewArrival && !hardcodedNavKeys.includes(c.key))
    .map((c) => c.key);

  const pages = [
    ...newArrivalKeys,
    ...navBeforeCustom,
    ...otherCustomKeys,
    ...navAfterCustom,
  ];

  const pageRoutes = {
    Wallpaper: '/item_group/001',
    Fabric: '/item_group/005',
  };

  const homewareContent = ['shop homeware online @ hertexhaus.co.za'];
  const rugsContent = ['shop rugs online @ hertexhaus.co.za'];
  const companyContent = [
    'About Us',
    'Explore Our Spaces',
    'Careers',
    ...(SHOW_REGISTER_NAV ? ['Register'] : []),
    ...companyAddOns,
  ];

  // A custom page is normally a leaf link with no dropdown. One that takes over a
  // hardcoded entry that has sub-pages (e.g. the custom "Fabric" page: Curtaining,
  // Upholstery, ...) keeps its dropdown: the label opens the page, the sub-pages
  // sit in the menu.
  const subPagesByKey: Record<string, string[]> = {
    Fabric: fabricsContent,
    Wallpaper: wallpaperContent,
    Brands: brandsContent,
    Company: companyContent,
  };
  const hasSubPages = (page: string) => (subPagesByKey[page]?.length ?? 0) > 0;
  const isLeafLink = (page: string) => !!customNavByKey[page] && !hasSubPages(page);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const getMenuItemContent = (page: string) => {
    setSelectedPage(page);
    switch (page) {
      case 'Fabric':
        setMenuContent(fabricsContent);
        break;
      case 'Company':
        setMenuContent(companyContent);
        break;
      case 'Brands':
        setMenuContent(brandsContent);
        break;
      case 'Homeware':
        setMenuContent(homewareContent);
        break;
      case 'Wallpaper':
        setMenuContent(wallpaperContent.sort());
        break;
      case 'Rugs':
        setMenuContent(rugsContent);
        break;
      default:
        setMenuContent([]);
    }
  };

  const handlePageClick = (page: string) => {
    if (selectedPage === page) {
      setSelectedPage(null);
    } else {
      setSelectedPage(page);
      setDisplayMobileItemMenu(true);
      getMenuItemContent(page);
    }
  };

  const applicationMapping: { [key: string]: string[] } = {
    'Curtaining': ['Curtaining'],
    'Upholstery': ['Upholstery'],
  };

  const findNavAddOnData = (displayName: string, parentCategory: string): NavTreeNode | null => {
    const parentNode = navigationItems.find(item => item.category_name === parentCategory);
    if (!parentNode || !parentNode.children) return null;

    return parentNode.children.find(
      child =>
        child.category_type === 'nav_add_on' &&
        (child.display_name === displayName || child.category_name === displayName)
    ) || null;
  };

  // Open a nav add-on. Internal links (same site) navigate in the same tab;
  // only genuinely external links open a new tab.
  const openNavAddOn = (
    url?: string | null,
    categoryId?: string | null
  ) => {
    if (url) {
      const internalHosts = [
        'hertex-staging.net',
        'hertex.co.za',
        'www.hertex.co.za',
      ];
      try {
        const parsed = new URL(url, window.location.origin);
        const isInternal =
          parsed.origin === window.location.origin ||
          internalHosts.includes(parsed.hostname);
        if (isInternal) {
          navigate(parsed.pathname + parsed.search);
          return;
        }
        window.open(url, '_blank');
      } catch {
        navigate(url);
      }
      return;
    }
    if (categoryId) {
      navigate(`/category/${categoryId}`);
    }
  };

  const handleNavigate = (parentPage: string, category: string) => {
    // 1. Check nav_add_on first
    const navAddOnData = findNavAddOnData(category, parentPage);
    if (navAddOnData) {
      openNavAddOn(
        navAddOnData.consumer_link_url,
        navAddOnData.consumer_link_category_id
      );
      return;
    }

    // 2. Check if this is a custom CMS page — must run before the switch so
    //    hardcoded cases like 'Curtaining' don't override custom page entries
    const parentNode = navigationItems.find(item => item.category_name === parentPage);
    const customChildNode = parentNode?.children?.find(
      child =>
        (child.category_name === category || child.display_name === category) &&
        child.category_type === 'custom'
    );
    if (customChildNode) {
      const slug = category.toLowerCase().replace(/\s+/g, '-');
      navigate(`/${slug}`);
      return;
    }

    // 3. Regular navigation
    switch (category) {
      case 'Fabric':
        navigate(`../item_group/${ItemGroupCode.FABRIC}`);
        break;
      case 'Curtaining':
      case 'Upholstery': {
        const formattedCategory = category.toLowerCase();
        const applications = applicationMapping[category] || [category];
        const searchParams = new URLSearchParams();
        applications.forEach((app) => searchParams.append('application', app));
        navigate(`../item_group/005/application/${formattedCategory}?${searchParams.toString()}`);
        break;
      }
      case 'About Us':
        navigate('../about-us');
        break;
      case 'Explore Our Spaces':
        navigate('../store-locator');
        break;
      case 'Careers':
        navigate('../careers');
        break;
      case 'Register':
        navigate('/register');
        break;
      case 'shop rugs online @ hertexhaus.co.za':
        window.open('https://hertexhaus.co.za/pages/rugs', '_blank');
        break;
      case 'shop homeware online @ hertexhaus.co.za':
        window.open('https://hertexhaus.co.za/', '_blank');
        break;
      case 'Hertex Wallpaper':
        navigate('/hertex-wallpaper');
        break;
      case 'Wallpaper Calculator':
        navigate('../wallpaper-calculator');
        break;
      case 'HAUS':
        window.open('https://hertexhaus.co.za/', '_blank');
        break;
      case 'Internationals':
        navigate('/internationals');
        break;
      default:
        if (category) {
          posthog.capture('brand_clicked', {
            brand_name: category,
            source: 'consumer',
          });
          const encodedBrand = encodeURIComponent(category);
          navigate(`../item_group/9000/brand/${encodedBrand}`);
        }
        break;
    }
  };

  return (
    <>
      <AppBar position="sticky" color="secondary" sx={{ boxShadow: '0' }}>
        <Box sx={{ position: 'relative' }}>
          <Toolbar disableGutters>
            <Box
              sx={{
                display: {
                  xs: 'none',
                  lg: 'flex',
                  alignItems: 'left',
                },
                ml: '2%',
                width: { md: '15%', lg: '10%' },
              }}
            >
              <img
                style={{
                  width: '100%',
                  cursor: 'pointer',
                }}
                src={navLogo}
                alt="Nav logo"
                onClick={() => {
                  navigate('/');
                }}
              />
            </Box>

            {/* Mobile View */}
            <Box
              sx={{
                width: '100%',
                display: { xs: 'flex', lg: 'none' },
                justifyContent: 'space-between',
                px: '10px',
              }}
            >
              <Box
                sx={{
                  display: {
                    xs: 'flex',
                    lg: 'none',
                  },
                  width: '150px',
                  justifySelf: 'center',
                  alignSelf: 'center',
                  mr: 2,
                }}
              >
                <img
                  style={{
                    width: '100%',
                    cursor: 'pointer',
                  }}
                  src={navLogo}
                  alt="Nav logo"
                  onClick={() => {
                    navigate('/');
                  }}
                />
              </Box>
              <Box>
                <IconButton
                  sx={{ padding: '1rem' }}
                  size="large"
                  aria-label="show search modal"
                  color="inherit"
                  onClick={() => handleOpenModal()}
                >
                  <SearchOutlined />
                </IconButton>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  {anchorElNav ? <Clear /> : <MenuIcon />}
                </IconButton>
              </Box>
              {/* Mobile Menu */}
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                marginThreshold={0}
                keepMounted
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: {
                    xs: 'block',
                    lg: 'none',
                  },
                }}
                elevation={0}
                PaperProps={{
                  style: {
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    maxHeight: 'calc(100vh - 50px)',
                    marginTop: '50px',
                    left: 0,
                    right: 0,
                    borderRadius: '0',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  },
                }}
                TransitionComponent={Slide}
                transitionDuration={300}
              >
                <Box>
                  <MenuList>
                    {pages.map((page) => (
                      <MenuItem
                        key={page}
                        onClick={() => {
                          if (customNavByKey[page]) {
                            navigate(customNavByKey[page].route);
                            handleCloseNavMenu();
                            return;
                          }
                          handlePageClick(page);
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box>
                              <ListItemText>
                                <Typography
                                  sx={{
                                    fontFamily: 'sans-serif',
                                    fontWeight: 'semibold',
                                    fontSize: '14px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1.68px',
                                  }}
                                >
                                  {customNavByKey[page] ? customNavByKey[page].label : page}
                                </Typography>
                              </ListItemText>
                            </Box>
                            {/* Custom pages are leaf links — no expand arrow — unless they
                                have sub-pages: then the row opens the page and the arrow
                                opens the sub-pages */}
                            {(!customNavByKey[page] || hasSubPages(page)) && (
                              <Box
                                onClick={
                                  customNavByKey[page]
                                    ? (e) => {
                                        e.stopPropagation();
                                        handlePageClick(page);
                                      }
                                    : undefined
                                }
                              >
                                <ListItemIcon>
                                  <KeyboardArrowDown fontSize="small" />
                                </ListItemIcon>
                              </Box>
                            )}
                          </Box>
                          <Box
                            sx={{
                              background: '#EEEEEE',
                              width: '100%',
                              pl: '20px',
                              mt: '10px',
                              overflow: 'hidden',
                            }}
                          >
                            {selectedPage === page && (
                              <MenuList>
                                {menuContent.map((elem: string) => (
                                  <MenuItem
                                    key={elem}
                                    onClick={() => {
                                      handleNavigate(selectedPage!, elem);
                                      handleCloseNavMenu();
                                    }}
                                    sx={{
                                      p: '0',
                                    }}
                                  >
                                    <ListItemText>
                                      <Typography
                                        sx={{
                                          fontFamily: 'sans-serif',
                                          fontWeight: 'bold',
                                          textTransform: 'uppercase',
                                          fontSize: '10px',
                                          textAlign: 'left',
                                          width: '100%',
                                          whiteSpace: 'nowrap',
                                          letterSpacing: '1.68px',
                                        }}
                                      >
                                        {elem}
                                      </Typography>
                                    </ListItemText>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </MenuList>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      px: '10px',
                    }}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      disableElevation
                      onClick={handleTradeLoginClick}
                      sx={{
                        mb: '10px',
                      }}
                    >
                      Trade Login
                    </Button>
                  </Box>
                </Box>
              </Menu>
            </Box>

            {/* Desktop Menu */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', lg: 'flex' },
                justifyContent: 'center',
                gap: '2%',
              }}
            >
              {pages.map((page, index: number) => (
                <Box key={index}>
                  <Button
                    key={page}
                    onClick={(e) => {
                      handleCloseNavMenu();
                      if (customNavByKey[page]) {
                        navigate(customNavByKey[page].route);
                        return;
                      }
                      if (page === 'Wallpaper') {
                        navigate('/hertex-wallpaper');
                        return;
                      }
                      navigate(pageRoutes[page as keyof typeof pageRoutes]);
                    }}
                    onMouseEnter={() => {
                      // Custom pages are leaf links — no mega menu — unless they have sub-pages
                      if (isLeafLink(page)) {
                        setDisplayMegaMenu(false);
                      } else {
                        setDisplayMegaMenu(true);
                        getMenuItemContent(page);
                      }
                    }}
                    sx={{
                      fontFamily: 'Sans-serif',
                      fontSize: '14px',
                      fontWeight: '300',
                      px: '5px',
                      letterSpacing: '3px',
                    }}
                  >
                    {customNavByKey[page] ? customNavByKey[page].label : page}
                  </Button>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: { xs: 'none', lg: 'flex' },
                mr: '2%',
              }}
            >
            <Box
              onClick={() => handleOpenModal()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Sans-serif',
                  fontSize: '14px',
                  fontWeight: '300',
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                }}
              >
                Search
              </Typography>
              <IconButton
                sx={{ padding: '1rem' }}
                size="large"
                aria-label="show search modal"
                color="inherit"
              >
                <SearchOutlined />
              </IconButton>
            </Box>

              <Box sx={{ display: 'flex', height: '100%' }}>
                <Button
                  disableElevation
                  aria-label="login"
                  onClick={handleTradeLoginClick}
                  startIcon={<PersonOutlined />}
                >
                  <Box>Trade Login</Box>
                </Button>
              </Box>
            </Box>
          </Toolbar>

          {/* Mega Menu Content */}
          {displayMegaMenu && (
            <Box
              sx={{
                width: '100%',
                position: 'absolute',
                top: '100%',
                zIndex: '999',
                backgroundColor: '#F9FAFB',
                display: 'flex',
                padding: '2rem',
              }}
              onMouseLeave={() => {
                setDisplayMegaMenu(false);
              }}
            >
              <Grid container justifyContent="center">
                {menuContent?.map(
                  (elem: string, index: number) =>
                    elem !== 'Fabric' && (
                      <Grid
                        key={index}
                        item
                        xs={3}
                        spacing={2}
                        sx={{
                          backgroundColor: '#fff',
                          height: '2rem',
                          margin: '0.5rem 0.5rem',
                          padding: '2rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          fontSize: '0.8rem',
                          lineHeight: '1.75rem',
                          letterSpacing: '0.12rem',
                        }}
                        onClick={() => {
                          handleNavigate(selectedPage!, elem);
                        }}
                      >
                        {elem}
                      </Grid>
                    )
                )}
              </Grid>
            </Box>
          )}
        </Box>
      </AppBar>
      <SearchModal
        modalIsOpen={modalIsOpen}
        handleOpenModal={handleOpenModal}
        handleCloseModal={handleCloseModal}
      />
    </>
  );
}

export default Navbar;